import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
    Loader2,
    RefreshCw,
    Send,
    XCircle,
    CheckCircle2,
    Clock,
    AlertTriangle,
    DollarSign,
    Calendar,
    Hourglass,
} from "lucide-react";

const authHeaders = () => {
    const t = localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function timeUntil(iso, now) {
    if (!iso || !now) return "—";
    const d = new Date(iso).getTime() - new Date(now).getTime();
    if (d < 0) return "Past due";
    const h = Math.floor(d / 3_600_000);
    if (h > 48) return `${Math.floor(h / 24)}d`;
    return `${h}h ${Math.floor((d % 3_600_000) / 60_000)}m`;
}

const STATUS_PILL = {
    due: { color: "bg-gold/30 text-burnt", icon: Hourglass, label: "Balance due" },
    paid: { color: "bg-mint text-forest", icon: CheckCircle2, label: "Paid in full" },
    forfeit: { color: "bg-burnt/20 text-burnt", icon: XCircle, label: "Forfeit" },
    refunded: { color: "bg-muted2/20 text-muted2", icon: XCircle, label: "Refunded" },
};

export default function AdminPreOrders() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [actionBusy, setActionBusy] = useState({});

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const res = await axios.get(`${API}/admin/preorders`, { headers: authHeaders() });
            setData(res.data);
        } catch {
            toast.error("Couldn't load pre-orders");
            setData({ preorders: [], stats: {}, now: new Date().toISOString() });
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const sendReminder = async (orderId, isFinal) => {
        setActionBusy((s) => ({ ...s, [orderId]: "reminder" }));
        try {
            const res = await axios.post(
                `${API}/admin/preorders/${orderId}/reminder?is_final=${isFinal}`,
                {},
                { headers: authHeaders() },
            );
            if (res.data?.ok) toast.success(`${isFinal ? "Final" : "First"} reminder sent`);
            else toast.error("Reminder send failed");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reminder failed");
        } finally {
            setActionBusy((s) => ({ ...s, [orderId]: null }));
        }
    };

    const cancel = async (orderId, refund) => {
        const reason = window.prompt(
            refund
                ? "Reason for cancel + refund? (will be visible to the customer)"
                : "Reason for cancel (deposit FORFEITED per policy)? Visible to customer.",
            "",
        );
        if (reason === null) return;
        const confirmMsg = refund
            ? "Cancel this pre-order AND refund the deposit on Stripe? This cannot be undone."
            : "Cancel this pre-order? The deposit will be FORFEITED (not refunded). The customer will be emailed.";
        if (!window.confirm(confirmMsg)) return;
        setActionBusy((s) => ({ ...s, [orderId]: refund ? "refund" : "forfeit" }));
        try {
            const res = await axios.post(
                `${API}/admin/preorders/${orderId}/cancel`,
                { refund_deposit: refund, reason },
                { headers: authHeaders() },
            );
            if (res.data?.ok) {
                toast.success(
                    refund
                        ? `Refunded $${(res.data.refund_amount || 0).toFixed(2)}`
                        : "Pre-order canceled (deposit forfeited)",
                );
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cancel failed");
        } finally {
            setActionBusy((s) => ({ ...s, [orderId]: null }));
        }
    };

    if (!data) {
        return (
            <div className="flex items-center gap-2 text-muted2">
                <Loader2 className="animate-spin" size={16} /> Loading pre-orders…
            </div>
        );
    }

    const { preorders, stats, now } = data;

    return (
        <div data-testid="admin-preorders-panel">
            <div className="flex items-center justify-between mb-5">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                    {preorders.length} pre-order{preorders.length === 1 ? "" : "s"}
                </p>
                <button
                    data-testid="admin-preorders-refresh"
                    onClick={load}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-forest hover:text-forestDark disabled:opacity-50"
                >
                    <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Stat label="Total" value={stats.total ?? 0} accent="text-navy" />
                <Stat label="Balance due" value={stats.due ?? 0} accent="text-burnt" />
                <Stat label="Paid in full" value={stats.paid ?? 0} accent="text-forest" />
                <Stat label="Forfeit / refunded" value={(stats.forfeit ?? 0) + (stats.refunded ?? 0)} accent="text-muted2" />
            </div>

            {preorders.length === 0 ? (
                <div className="bg-white rounded-[20px] p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                    <Hourglass size={36} className="mx-auto text-mint" />
                    <p className="mt-3 font-bold text-navy">No pre-orders yet</p>
                    <p className="mt-1 text-sm text-muted2">
                        Orders with delivery 7+ days out will appear here.
                    </p>
                </div>
            ) : (
                <ul className="space-y-3">
                    {preorders.map((o) => {
                        const pill = STATUS_PILL[o.balance_status] || STATUS_PILL.due;
                        const IconC = pill.icon;
                        const customer = o.customer || {};
                        const isBusy = !!actionBusy[o.order_id];
                        return (
                            <li
                                key={o.order_id}
                                data-testid={`preorder-${o.order_id}`}
                                className="bg-white rounded-2xl p-4 shadow-[0_4px_18px_rgba(0,0,0,0.06)]"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-xs text-muted2">
                                                #{o.order_id.slice(0, 8).toUpperCase()}
                                            </span>
                                            <Pill color={pill.color}>
                                                <IconC size={11} /> {pill.label}
                                            </Pill>
                                        </div>
                                        <p className="mt-1 font-bold text-navy break-all">
                                            {customer.name} · {customer.email}
                                        </p>
                                        <div className="mt-2 grid sm:grid-cols-3 gap-y-1 gap-x-4 text-sm text-muted2">
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar size={12} /> Delivery {o.delivery_date}
                                            </span>
                                            <span className="inline-flex items-center gap-1">
                                                <DollarSign size={12} />
                                                Dep ${Number(o.deposit_amount).toFixed(2)} · Bal{" "}
                                                <b className="text-burnt tabular-nums">
                                                    ${Number(o.balance_amount).toFixed(2)}
                                                </b>
                                            </span>
                                            {o.balance_status === "due" && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Clock size={12} /> Due in{" "}
                                                    <b className="text-navy">
                                                        {timeUntil(o.balance_due_at, now)}
                                                    </b>
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-[11px] text-muted2 flex gap-3 flex-wrap">
                                            <span>R1: {o.reminder_1_sent_at ? fmtDate(o.reminder_1_sent_at) : "—"}</span>
                                            <span>R2: {o.reminder_2_sent_at ? fmtDate(o.reminder_2_sent_at) : "—"}</span>
                                        </div>
                                    </div>
                                </div>

                                {o.balance_status === "due" && (
                                    <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-mint">
                                        <ActionBtn
                                            testId={`send-r1-${o.order_id}`}
                                            onClick={() => sendReminder(o.order_id, false)}
                                            busy={actionBusy[o.order_id] === "reminder"}
                                            disabled={isBusy}
                                            color="bg-forest text-white hover:bg-forestDark"
                                        >
                                            <Send size={12} /> Send first reminder
                                        </ActionBtn>
                                        <ActionBtn
                                            testId={`send-r2-${o.order_id}`}
                                            onClick={() => sendReminder(o.order_id, true)}
                                            busy={actionBusy[o.order_id] === "reminder"}
                                            disabled={isBusy}
                                            color="bg-burnt text-white hover:bg-burnt/90"
                                        >
                                            <AlertTriangle size={12} /> Send final reminder
                                        </ActionBtn>
                                        <ActionBtn
                                            testId={`cancel-forfeit-${o.order_id}`}
                                            onClick={() => cancel(o.order_id, false)}
                                            busy={actionBusy[o.order_id] === "forfeit"}
                                            disabled={isBusy}
                                            color="bg-white border border-burnt text-burnt hover:bg-burnt/10"
                                        >
                                            <XCircle size={12} /> Cancel (forfeit deposit)
                                        </ActionBtn>
                                        <ActionBtn
                                            testId={`cancel-refund-${o.order_id}`}
                                            onClick={() => cancel(o.order_id, true)}
                                            busy={actionBusy[o.order_id] === "refund"}
                                            disabled={isBusy}
                                            color="bg-white border border-muted2 text-muted2 hover:bg-mint/30"
                                        >
                                            <XCircle size={12} /> Cancel + refund deposit
                                        </ActionBtn>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="bg-white rounded-[16px] p-4 shadow-[0_4px_16px_rgba(0,0,0,0.05)]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted2">{label}</p>
            <p className={`mt-1 font-serif font-black text-2xl ${accent}`}>{value}</p>
        </div>
    );
}

function Pill({ children, color }) {
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${color}`}>
            {children}
        </span>
    );
}

function ActionBtn({ children, onClick, busy, disabled, color, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            disabled={disabled || busy}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${color}`}
        >
            {busy ? <Loader2 size={12} className="animate-spin" /> : children}
        </button>
    );
}
